// @ts-nocheck
import React, { useState } from 'react';
import { useFilterPresets, type FilterPreset } from './useFilterPresets';

interface AdminFilterPresetsProps {
  tableId: string;
  currentQuickFilter?: string;
  currentDateRange?: { start: Date | null; end: Date | null };
  currentSearchRules?: any[];
  currentSearchOperator?: 'AND' | 'OR';
  onApplyPreset: (preset: FilterPreset) => void;
  onSaveCurrent: (
    name: string,
    quickFilter?: string,
    dateRange?: { start: Date | null; end: Date | null },
    searchRules?: any[],
    searchOperator?: 'AND' | 'OR'
  ) => void;
  className?: string;
}

export const AdminFilterPresets: React.FC<AdminFilterPresetsProps> = ({
  tableId,
  currentQuickFilter,
  currentDateRange,
  currentSearchRules,
  currentSearchOperator,
  onApplyPreset,
  onSaveCurrent,
  className = '',
}) => {
  const { presets, savePreset, deletePreset } = useFilterPresets(tableId);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [presetName, setPresetName] = useState('');

  const handleSave = () => {
    if (!presetName.trim()) return;

    savePreset({
      name: presetName.trim(),
      quickFilter: currentQuickFilter,
      dateRange: currentDateRange,
      searchRules: currentSearchRules,
      searchOperator: currentSearchOperator,
    });

    setPresetName('');
    setShowSaveDialog(false);
  };

  const hasActiveFilters =
    currentQuickFilter ||
    (currentDateRange?.start && currentDateRange?.end) ||
    (currentSearchRules && currentSearchRules.length > 0);

  return (
    <div className={`relative ${className}`}>
      <div className="flex items-center gap-2 flex-wrap">
        {presets.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">Presets:</span>
            {presets.map((preset) => (
              <button
                key={preset.id}
                onClick={() => onApplyPreset(preset)}
                className="px-3 py-1.5 text-sm bg-luna-secondary/10 hover:bg-luna-secondary/20 text-luna-secondary rounded-lg transition-colors min-h-[32px] touch-manipulation"
                aria-label={`Apply filter preset: ${preset.name}`}
              >
                {preset.name}
              </button>
            ))}
          </div>
        )}

        {hasActiveFilters && (
          <button
            onClick={() => setShowSaveDialog(true)}
            className="px-3 py-1.5 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-1 transition-colors min-h-[32px] touch-manipulation"
            aria-label="Save current filters as preset"
          >
            <span className="material-symbols-outlined text-sm">bookmark_add</span>
            <span>Save Preset</span>
          </button>
        )}
      </div>

      {/* Save Dialog */}
      {showSaveDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
          <div className="bg-white dark:bg-luna-primary rounded-xl border-2 border-luna-secondary/30 p-6 max-w-md w-full shadow-xl">
            <h3 className="text-xl font-bold text-luna-text-light dark:text-luna-text-dark mb-4">
              Save Filter Preset
            </h3>
            <div className="space-y-4">
              <div>
                <label htmlFor="preset-name" className="block text-sm font-medium text-luna-text-light dark:text-luna-text-dark mb-1">
                  Preset Name
                </label>
                <input
                  id="preset-name"
                  type="text"
                  value={presetName}
                  onChange={(e) => setPresetName(e.target.value)}
                  placeholder="e.g., Active Admins This Month"
                  className="w-full px-4 py-2.5 rounded-lg border border-luna-secondary/30 bg-white dark:bg-luna-primary text-luna-text-light dark:text-luna-text-dark min-h-[44px] touch-manipulation"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSave();
                    } else if (e.key === 'Escape') {
                      setShowSaveDialog(false);
                    }
                  }}
                  autoFocus
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-2 pt-2">
                <button
                  onClick={() => {
                    setShowSaveDialog(false);
                    setPresetName('');
                  }}
                  className="flex-1 px-4 py-2.5 rounded-lg text-luna-text-light dark:text-luna-text-dark hover:bg-gray-100 dark:hover:bg-luna-secondary/10 transition-colors min-h-[44px] touch-manipulation"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={!presetName.trim()}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-luna-secondary text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:bg-luna-secondary/90 min-h-[44px] touch-manipulation"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preset Management (show delete buttons on hover) */}
      {presets.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {presets.map((preset) => (
            <div key={preset.id} className="group relative">
              <button
                onClick={() => onApplyPreset(preset)}
                className="px-3 py-1.5 text-sm bg-luna-secondary/10 hover:bg-luna-secondary/20 text-luna-secondary rounded-lg transition-colors min-h-[32px] touch-manipulation"
              >
                {preset.name}
              </button>
              <button
                onClick={() => deletePreset(preset.id)}
                className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs transition-opacity touch-manipulation"
                aria-label={`Delete preset: ${preset.name}`}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

